// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports
import { ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';
// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports
import {
	Experience,
	EXPERIENCE_ID,
	ExperienceCheckTimeout,
} from '@atlaskit/editor-common/experiences';
// eslint-disable-next-line @atlaskit/platform/no-barrel-entry-imports
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import type { SetThinkingStateAction } from '../editor-actions/types';
import type { ThinkingPluginState } from '../pm-plugins/thinking-plugin';

// A fixed reliability deadline: a review that never reaches a terminal status must fail.
export const REVIEW_GENERATION_TIMEOUT_MS = 300_000;

export type ReviewGenerationExperience = {
	destroy: () => boolean;
	failure: (reason: string) => boolean;
	onThinkingStatusChange: (state: ThinkingPluginState) => void;
	start: (options: Parameters<SetThinkingStateAction>[1]) => void;
};

/** Tracks Review attempts through the thinking bar's committed terminal status. */
export function createReviewGenerationExperience(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
): ReviewGenerationExperience {
	let entryPoint: string | undefined;
	let experience: Experience | undefined;
	const createExperience = (
		id: typeof EXPERIENCE_ID.TOOLBAR_ACTION | typeof EXPERIENCE_ID.MENU_ACTION,
	): Experience =>
		new Experience(id, {
			action: 'generateSuggestions',
			actionSubjectId: ACTION_SUBJECT.AI_SUGGESTIONS,
			// Count every attempt during the initial rollout, including retries.
			sampleRate: 1,
			dispatchAnalyticsEvent: (event) => api?.analytics?.actions.fireAnalyticsEvent(event),
			checks: [new ExperienceCheckTimeout({ durationMs: REVIEW_GENERATION_TIMEOUT_MS })],
		});
	const toolbarExperience = createExperience(EXPERIENCE_ID.TOOLBAR_ACTION);
	const sidebarExperience = createExperience(EXPERIENCE_ID.MENU_ACTION);

	return {
		start: (options: Parameters<SetThinkingStateAction>[1]) => {
			if (options?.entryPoint || !options?.isRetry) {
				experience?.abort({ reason: 'reviewReplaced' });
				entryPoint = options?.entryPoint;
				experience =
					entryPoint === 'primaryToolbar'
						? toolbarExperience
						: entryPoint === 'suggestionsPanelHeader' || entryPoint === 'suggestionsPanelEmptyState'
							? sidebarExperience
							: undefined;
			}
			experience?.start({
				forceRestart: true,
				method: entryPoint,
				metadata: { isRetry: options?.isRetry ?? false },
			});
		},
		onThinkingStatusChange: ({ status, error }: ThinkingPluginState) => {
			if (status === 'complete') {
				experience?.success();
			} else if (status === 'error') {
				experience?.failure({
					reason: 'generationFailed',
					metadata: { template: error?.template },
				});
			} else if (status === 'idle') {
				experience?.abort({ reason: 'thinkingStopped' });
				entryPoint = undefined;
				experience = undefined;
			}
		},
		failure: (reason: string) => experience?.failure({ reason }) ?? false,
		destroy: () => experience?.abort({ reason: 'editorDestroyed' }) ?? false,
	};
}
